import { useEffect, useState } from "react";

import liff from "@line/liff";
import axios from "axios";
import LoadingScreen from "../Loading/LoadingScreen";

function ThemeMain({ config }) {
  const {
    liffId,
    apiBaseUrl,
    flexImage,
    id = "1",
    title = "สุ่มกาชาปอง",
    subtitle = "ลุ้นรับของรางวัลสุดพิเศษ",
  } = config;

  const game = require(`../../Images/${id}.png`);
  const [profileLine, setProfileLine] = useState(null);
  const [data, setData] = useState(null);
  const [listRewards, setListRewards] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameError, setGameError] = useState("");
  const [rewardsError, setRewardsError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let isMounted = true;

    const initializeLiff = async () => {
      try {
        await liff.init({ liffId });
        if (!liff.isLoggedIn()) {
          liff.login();
          return;
        }

        const profile = await liff.getProfile();
        if (isMounted) {
          setProfileLine(profile.userId);
        }
      } catch (error) {
        console.error("LIFF Error:", error);
        if (isMounted) {
          setGameError("เข้าสู่ระบบไม่สำเร็จ กรุณาปิดแล้วเปิดเกมจาก LINE อีกครั้ง");
          setLoading(false);
        }
      }
    };

    initializeLiff();
    return () => {
      isMounted = false;
    };
  }, [liffId]);

  useEffect(() => {
    if (!profileLine) return undefined;

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(apiBaseUrl, {
          params: { path: "getData", userId: profileLine },
        });
        if (response.data.status === 200) {
          if (isMounted) {
            setData(response.data.data);
            setGameError("");
          }
        } else if (isMounted) {
          setGameError(response.data.message || "โหลดข้อมูลการเล่นไม่สำเร็จ");
        }
      } catch (error) {
        console.error("Get player data error:", error);
        if (isMounted) {
          setGameError("เชื่อมต่อข้อมูลการเล่นไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [profileLine, apiBaseUrl]);

  useEffect(() => {
    let isMounted = true;
    const fetchRewards = async () => {
      try {
        const response = await axios.get(apiBaseUrl, {
          params: { path: "getRewards" },
        });
        if (response.data.status === 200 && isMounted) {
          setListRewards(response.data.data);
          setRewardsError("");
        } else if (isMounted) {
          setRewardsError(response.data.message || "โหลดรายการรางวัลไม่สำเร็จ");
        }
      } catch (error) {
        console.error("Get rewards error:", error);
        if (isMounted) setRewardsError("ไม่สามารถโหลดรายการรางวัลได้ในขณะนี้");
      }
    };

    fetchRewards();
    return () => {
      isMounted = false;
    };
  }, [apiBaseUrl]);

  const hasReachedLimit =
    data?.value !== undefined &&
    data?.total !== undefined &&
    Number(data.value) >= Number(data.total);
  const canPlay = Boolean(profileLine && data && !loading && !isPlaying && !hasReachedLimit);

  const randomRewards = async () => {
    if (!canPlay) return;

    setIsPlaying(true);
    setRewards(null);
    setGameError("");
    setNotice("");

    try {
      const [response] = await Promise.all([
        axios.get(apiBaseUrl, {
          params: { path: "play", userId: profileLine },
        }),
        new Promise((resolve) => window.setTimeout(resolve, 1400)),
      ]);

      if (response.data.status === 200) {
        setRewards(response.data);
        if (response.data.data) {
          setData(response.data.data);
        } else if (data?.value !== undefined) {
          setData({ ...data, value: Number(data.value) + 1 });
        }

        liff
          .sendMessages([
            {
              type: "flex",
              altText: "ยินดีด้วยคุณได้รับรางวัล คลิกเพื่อดู",
              contents: {
                type: "bubble",
                hero: {
                  type: "image",
                  url: flexImage,
                  size: "full",
                  aspectRatio: "20:13",
                  aspectMode: "cover",
                },
                body: {
                  type: "box",
                  layout: "vertical",
                  contents: [
                    {
                      type: "text",
                      text: "แทนคำขอบคุณ",
                      weight: "bold",
                      size: "xl",
                    },
                    {
                      type: "box",
                      layout: "vertical",
                      contents: [
                        {
                          type: "text",
                          text: `ยินดีด้วยคุณได้รับ ${response.data.reward.name}`,
                          size: "lg",
                          color: "#B3B3B3",
                          wrap: true,
                        },
                      ],
                    },
                  ],
                },
              },
            },
          ])
          .then(() => setNotice("ส่งรายละเอียดรางวัลไปยังแชท LINE แล้ว"))
          .catch((error) => {
            console.error("Send reward message error:", error);
            setNotice("รับรางวัลแล้ว แต่ไม่สามารถส่งข้อความเข้าแชท LINE ได้");
          });
      } else if (response.data.status === 204) {
        setNotice(response.data.message || "คุณใช้สิทธิ์สุ่มรางวัลครบแล้ว");
        if (data?.total !== undefined) {
          setData({ ...data, value: data.total });
        }
      } else {
        setGameError(response.data.message || "สุ่มรางวัลไม่สำเร็จ กรุณาลองอีกครั้ง");
      }
    } catch (error) {
      console.error("Play request error:", error);
      setGameError("เชื่อมต่อระบบสุ่มไม่สำเร็จ กรุณาลองอีกครั้ง");
    } finally {
      setIsPlaying(false);
    }
  };

  const usedCount = Number(data?.value || 0);
  const totalCount = Number(data?.total || 0);
  const remainingCount = Math.max(totalCount - usedCount, 0);

  return (
    <>
      {loading && <LoadingScreen />}
      <main className={`app-bg${id} turns-copy gacha-screen`} style={{ padding: "20px" }}>
        <div className="gacha-stars" aria-hidden="true">
          <span>✦</span>
          <span>✧</span>
          <span>✦</span>
          <span>✧</span>
          <span>✦</span>
          <span>✧</span>
        </div>

        <div className="gacha-content">
          <header className="gacha-header">
            <div className="gacha-brand"><span className="brand-orbit">✦</span> GACHA CLUB</div>
            <div className="gacha-live"><span /> SPECIAL DROP</div>
          </header>

          <section className="gacha-intro">
            <p className="gacha-eyebrow">A LITTLE MAGIC FOR YOU</p>
            <h1>{title}</h1>
            <p className="gacha-subtitle">{subtitle}</p>
          </section>

          <section className={`gacha-machine ${isPlaying ? "is-playing" : ""}`} aria-label="ตู้กาชาปอง">
            <div className="machine-glow" />
            <div className="machine-sparkle sparkle-one">✦</div>
            <div className="machine-sparkle sparkle-two">✧</div>
            <div className="machine-sparkle sparkle-three">✦</div>
            <img src={game} alt="ตู้กาชาปอง" className="machine-image" />
            <div className="machine-caption">
              <span>YOUR LUCKY CAPSULE</span>
              <span className="caption-dot">✦</span>
            </div>
            {isPlaying && (
              <div className="playing-label" role="status">
                กำลังสุ่มรางวัล...
              </div>
            )}
          </section>

          <section className="gacha-action">
            {data && (
              <div className="turns-meter" aria-label={`เหลือสิทธิ์ ${remainingCount} ครั้ง`}>
                <div className="turns-copy">
                  <span>สิทธิ์ของคุณ</span>
                  <strong>{remainingCount}<small> / {totalCount} ครั้ง</small></strong>
                </div>
                <div className="turns-track">
                  <span style={{ width: `${totalCount ? Math.min((usedCount / totalCount) * 100, 100) : 0}%` }} />
                </div>
              </div>
            )}

            {hasReachedLimit ? (
              <div className="gacha-limit" role="status">
                <span>♡</span>
                สิทธิ์การสุ่มของคุณหมดแล้ว
              </div>
            ) : (
              <button
                type="button"
                className="gacha-button"
                onClick={randomRewards}
                disabled={!canPlay}
              >
                <span>{isPlaying ? "กำลังเปิดแคปซูล..." : "หมุนกาชาปองเลย"}</span>
                <span className="button-arrow" aria-hidden="true">✦</span>
              </button>
            )}

            <p className="gacha-hint">
              {isPlaying ? "ขอให้โชคดีนะ!" : "กดปุ่มเพื่อสุ่มของรางวัล"}
            </p>
            {gameError && <p className="gacha-message is-error" role="alert">{gameError}</p>}
            {notice && <p className="gacha-message" role="status">{notice}</p>}
          </section>

          {rewards && (
            <section className="prize-reveal" aria-live="polite">
              <div className="prize-confetti" aria-hidden="true">✦　✧　✦</div>
              <p className="prize-label">CONGRATULATIONS!</p>
              <p className="prize-heading">คุณได้รับ</p>
              {rewards.reward?.image && (
                <img src={rewards.reward.image} alt="" className="prize-image" />
              )}
              <h2>{rewards.reward?.name}</h2>
            </section>
          )}

          <section className="prize-list">
            <div className="section-heading">
              <div>
                <p className="gacha-eyebrow">THE PRIZE COLLECTION</p>
                <h2>ของรางวัลทั้งหมด</h2>
              </div>
              <span className="prize-count">{listRewards?.length || 0} ITEMS</span>
            </div>

            {rewardsError ? (
              <p className="gacha-message is-error" role="alert">{rewardsError}</p>
            ) : (
              <div className="prize-grid">
                {listRewards?.map((reward, index) => (
                  <article className="prize-item" key={`${reward.name}-${index}`}>
                    <div className="prize-item-image">
                      {reward.image && <img src={reward.image} alt="" loading="lazy" />}
                    </div>
                    <p>{reward.name}</p>
                  </article>
                ))}
              </div>
            )}
          </section>

          <footer className="gacha-footer">MADE WITH A LITTLE MAGIC <span>✦</span></footer>
        </div>
      </main>
    </>
  );
}

export default ThemeMain;
